/* Google Places API (New) — autocomplete + details, ported from the mobile
 * app's `core/services/google_places_service.dart`.
 *
 * Two things here are billing decisions, not style choices:
 *
 *  1. ONE session token spans an autocomplete run and the details call that
 *     resolves the chosen prediction, and is regenerated immediately after the
 *     details call. Google bills a grouped session as a single "Autocomplete
 *     (with Place Details)" unit; drop the token and every keystroke becomes a
 *     separately-billed request — roughly 15x the cost for the same journey.
 *
 *  2. `details()` returns null rather than throwing, and callers must persist
 *     NOTHING on null. A prediction carries no coordinates, so falling back to
 *     it would write (0, 0) — a point in the Gulf of Guinea that quietly breaks
 *     every distance query the contractor ever appears in.
 *
 * The browser key is HTTP-referrer restricted, so it is a billing control
 * rather than a secret. When it is absent the service reports `enabled: false`
 * and the picker degrades to manual entry instead of firing doomed requests.
 */

import { config } from "./config";

const BASE = "https://places.googleapis.com/v1";

/** An autocomplete prediction: identity and text only, never coordinates. */
export interface PlacePrediction {
  placeId: string;
  /** The bold "main text" of the prediction — a suburb or place name. */
  name: string;
  /** The full prediction line, e.g. "Sea Point, Cape Town, South Africa". */
  formattedAddress: string;
}

/** A resolved place, with coordinates that are safe to persist. */
export interface PlaceDetails extends PlacePrediction {
  latitude: number;
  longitude: number;
  suburb?: string;
  city?: string;
}

/** Thrown by `autocomplete` only. Carries copy that is safe to show a user. */
export class PlacesError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PlacesError";
  }
}

function newSessionToken(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function textOf(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const text = (value as { text?: unknown }).text;
  return typeof text === "string" ? text : "";
}

function componentOf(components: unknown, wanted: string[]): string | undefined {
  if (!Array.isArray(components)) return undefined;
  for (const raw of components) {
    if (!raw || typeof raw !== "object") continue;
    const entry = raw as { types?: unknown; longText?: unknown };
    const types = Array.isArray(entry.types) ? entry.types : [];
    if (wanted.some((type) => types.includes(type))) {
      return typeof entry.longText === "string" ? entry.longText : undefined;
    }
  }
  return undefined;
}

export class PlacesService {
  private sessionToken = newSessionToken();

  constructor(private readonly apiKey: string = config.placesApiKey) {}

  /** False when no browser key is configured; callers must offer manual entry. */
  get enabled(): boolean {
    return this.apiKey.length > 0;
  }

  /**
   * Address predictions restricted to South Africa.
   *
   * An empty array means "no matches" and never "something went wrong" — a
   * failure is a thrown `PlacesError`, so the picker can tell the two apart and
   * only offer a retry for the second.
   */
  async autocomplete(query: string, signal?: AbortSignal): Promise<PlacePrediction[]> {
    const input = query.trim();
    if (input.length < 3) return [];
    if (!this.enabled) throw new PlacesError("Address search isn't available right now.");

    let response: Response;
    try {
      response = await fetch(`${BASE}/places:autocomplete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": this.apiKey,
        },
        body: JSON.stringify({
          input,
          includedRegionCodes: ["za"],
          sessionToken: this.sessionToken,
        }),
        ...(signal ? { signal } : {}),
      });
    } catch (cause) {
      if (signal?.aborted) throw cause;
      throw new PlacesError("We couldn't reach address search. Check your connection.");
    }

    if (!response.ok) {
      // A referrer-restricted key answers 403 for an origin that was never
      // allowlisted, which is a deployment mistake rather than a user one —
      // hence copy that points at "try again later", not at their typing.
      throw new PlacesError("Address search is unavailable right now. Please try again.");
    }

    const payload = (await response.json()) as { suggestions?: unknown };
    const suggestions = Array.isArray(payload.suggestions) ? payload.suggestions : [];

    const seen = new Set<string>();
    const predictions: PlacePrediction[] = [];

    for (const raw of suggestions) {
      if (!raw || typeof raw !== "object") continue;
      const prediction = (raw as { placePrediction?: unknown }).placePrediction;
      if (!prediction || typeof prediction !== "object") continue;

      const entry = prediction as { placeId?: unknown; text?: unknown; structuredFormat?: unknown };
      const placeId = typeof entry.placeId === "string" ? entry.placeId : "";
      if (!placeId || seen.has(placeId)) continue;
      seen.add(placeId);

      const structured = entry.structuredFormat as { mainText?: unknown } | undefined;
      const mainText = textOf(structured?.mainText);
      const fullText = textOf(entry.text);

      predictions.push({
        placeId,
        name: mainText || fullText,
        formattedAddress: fullText || mainText,
      });
    }

    return predictions;
  }

  /**
   * Resolves a prediction to coordinates.
   *
   * Returns null on any failure — including a 200 that somehow carries no
   * location — so that the only way to obtain a stored area is a genuinely
   * geocoded one.
   */
  async details(placeId: string): Promise<PlaceDetails | null> {
    if (!this.enabled || !placeId) return null;

    try {
      const url = new URL(`${BASE}/places/${encodeURIComponent(placeId)}`);
      url.searchParams.set("sessionToken", this.sessionToken);

      const response = await fetch(url.toString(), {
        headers: {
          "X-Goog-Api-Key": this.apiKey,
          "X-Goog-FieldMask": "id,location,addressComponents,formattedAddress,displayName",
        },
      });
      if (!response.ok) return null;

      const data = (await response.json()) as {
        id?: unknown;
        location?: { latitude?: unknown; longitude?: unknown };
        addressComponents?: unknown;
        formattedAddress?: unknown;
        displayName?: unknown;
      };

      const latitude = typeof data.location?.latitude === "number" ? data.location.latitude : 0;
      const longitude = typeof data.location?.longitude === "number" ? data.location.longitude : 0;
      if (latitude === 0 && longitude === 0) return null;

      const formattedAddress = typeof data.formattedAddress === "string" ? data.formattedAddress : "";
      const displayName = textOf(data.displayName);

      return {
        placeId: typeof data.id === "string" ? data.id : placeId,
        name: displayName || formattedAddress,
        formattedAddress: formattedAddress || displayName,
        latitude,
        longitude,
        suburb: componentOf(data.addressComponents, [
          "sublocality",
          "sublocality_level_1",
          "neighborhood",
        ]),
        city: componentOf(data.addressComponents, ["locality"]),
      };
    } catch {
      return null;
    } finally {
      // The details call closes the billing session whether or not it
      // succeeded; reusing the token after it would be billed per request.
      this.sessionToken = newSessionToken();
    }
  }
}
